import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Grid2 as Grid, Avatar, TextField, IconButton, CircularProgress } from '@mui/material';
import { Delete, Download, AddAPhoto } from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { Actions, useDispatchProvider } from '../utils/StateProvider';

const FieldImage = ({ control, name, label, rules, setValue, getValues, watch, disabled }) => {
  const dispatch = useDispatchProvider();
  const [preview, setPreview] = useState(undefined);
  const [uploading, setUploading] = useState(false);

  const value = watch(name);

  useEffect(() => {
    setPreview(value);
  }, [value]);

  const handleDeleteFile = () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: { open: true, title: 'Are you sure you want to delete this file?', onConfirm: deleteFile },
      });
  };

  const deleteFile = async () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: {
          open: false,
          title: '',
          onConfirm: () => {},
        },
      });

    try {
      const storageRef = ref(storage, getValues(name));

      await deleteObject(storageRef);
      setValue(name, '');
      setPreview('');
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'success',
            message: `Image deleted successfully!`,
          },
        });
    } catch (error) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error deleting image: ${error}`,
          },
        });
      setValue(name, '');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    const extension = file.name.split('.').pop();
    const uniqueName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${extension}`;
    const storageRef = ref(storage, `products/${uniqueName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Progress handling (optional)
      },
      (error) => {
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'error',
              message: `Upload error: ${error}`,
            },
          });
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'success',
            message: `File upload successful: ${downloadURL}`,
          },
        });
        setUploading(false);
        setValue(name, downloadURL);
        setPreview(downloadURL);
      },
    );
  };

  return (
    <Grid
      container
      spacing={2}
      alignItems={'center'}
    >
      <Grid size={'grow'}>
        <Controller
          name={name}
          defaultValue={''}
          control={control}
          rules={rules}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={label}
              fullWidth
              variant="filled"
              disabled={true}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Grid>
      {preview && (
        <Grid size={'auto'}>
          <Avatar
            alt="Image"
            src={preview}
            variant={'rounded'}
            sx={{ width: 56, height: 56 }}
          />
        </Grid>
      )}
      <Grid size={'auto'}>
        <Grid
          container
          spacing={0}
          alignItems={'center'}
        >
          {!preview && (
            <Grid size={'auto'}>
              <IconButton
                size="large"
                // onClick={handleUploadFile}
                component={'label'}
                disabled={disabled || uploading}
              >
                <AddAPhoto />
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </IconButton>
            </Grid>
          )}
          {preview && (
            <>
              <Grid size={'auto'}>
                <IconButton
                  size="large"
                  component={'a'}
                  href={preview}
                  target="_blank"
                >
                  <Download />
                </IconButton>
              </Grid>
              <Grid size={'auto'}>
                <IconButton
                  size="large"
                  onClick={handleDeleteFile}
                  disabled={disabled}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </>
          )}
          {uploading && (
            <Grid size={'auto'}>
              <CircularProgress
                color={'primary'}
                size={24}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FieldImage;
